import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Alert, Card } from 'react-bootstrap';
import { FaInbox } from 'react-icons/fa';
import { Pagination } from '../commons/Pagination';
import Spinner from '../commons/Spinner';
import { FiltrosIngreso } from './FiltrosIngreso';
import { IngresoTable } from './IngresoTable';
import { ObservacionModal } from './ObservacionModal';
import { postEntryEquipmentFilters } from '../../api/common';
import './Ingresos.css';

moment.locale('es');

/**
 * Componente principal para listar ingresos de equipos
 * Incluye filtros avanzados, paginación y visualización profesional
 * @param {Object} ingresos - Objeto con resultados paginados
 * @param {Function} setIngreso - Función para actualizar ingresos
 * @param {Number} page - Página actual
 * @param {Function} setPage - Función para cambiar de página
 */
export const Listaringreso = ({ ingresos, setIngreso, page, setPage }) => {
  const navigate = useNavigate();
  const [filtrosActivos, setFiltrosActivos] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showObservacionModal, setShowObservacionModal] = useState(false);
  const [ingresoSeleccionado, setIngresoSeleccionado] = useState(null);
  const [filteredPage, setFilteredPage] = useState(1);
  const filteredPageSize = 10;

  /**
   * Maneja el cambio de página
   */
  const changePage = useCallback((newPage) => {
    setIngreso({ ...ingresos, results: [] });
    setPage(newPage);
  }, [ingresos, setIngreso, setPage]);

  /**
   * Navega al detalle de un ingreso
   */
  const handleOpenIngreso = useCallback((ingreso) => {
    navigate(`/Ingresos/${ingreso._id}?orden=${ingreso.numeroOrden}&origen=ingresos`);
  }, [navigate]);

  /**
   * Maneja la visualización de observaciones
   */
  const handleObservacion = useCallback((ingreso) => {
    setIngresoSeleccionado(ingreso);
    setShowObservacionModal(true);
  }, []);

  /**
   * Aplica filtros y consulta al backend
   */
  const handleFilter = useCallback(async (filtros) => {
    setIsLoading(true);
    const filtrosLimpios = filtros || {};
    const hasActiveFilters = Object.keys(filtrosLimpios).length > 0;
    setFiltrosActivos(hasActiveFilters ? filtrosLimpios : null);
    setFilteredPage(1);
    
    try {
      const payload = {
        filtros: filtrosLimpios,
        options: { 
          sort: { createdAt: -1 } 
        }
      };

      if (!hasActiveFilters) {
        payload.pagination = {
          page: 1,
          limit: 20
        };
      }

      const response = await postEntryEquipmentFilters(payload);
      
      if (response?.data.data.data) {
        const apiResults = Array.isArray(response.data.data.data) ? response.data.data.data : [];
        setIngreso({
          elements: apiResults.length,
          next: response?.data?.data?.next || null,
          perPage: response?.data?.data?.perPage || 20,
          prev: response?.data?.data?.prev || null,
          results: apiResults,
          total_elements: response?.data?.data?.total_elements || apiResults.length,
          total_pages: response?.data?.data?.total_pages || 1
        });

        if (!hasActiveFilters) {
          setPage(1);
        }
      }
    } catch (error) {
      console.error('Error al aplicar filtros:', error);
      alert('Error al aplicar filtros. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  }, [setIngreso, setPage]);

  /**
   * Limpia todos los filtros
   */
  const handleClearFilters = useCallback(() => {
    setFiltrosActivos(null);
    handleFilter({});
    setFilteredPage(1);
  }, [handleFilter]);

  /**
   * Lista de ingresos memoizada
   */
  const listaIngresos = useMemo(() => {
    if (!Array.isArray(ingresos?.results) || ingresos.results.length === 0) {
      return [];
    }
    return ingresos.results;
  }, [ingresos]);

  const isFiltering = useMemo(() => {
    return !!(filtrosActivos && Object.keys(filtrosActivos).length > 0);
  }, [filtrosActivos]);

  const totalFilteredPages = useMemo(() => {
    if (!isFiltering) return 1;
    return Math.max(1, Math.ceil(listaIngresos.length / filteredPageSize));
  }, [isFiltering, listaIngresos.length]);

  const pagedIngresos = useMemo(() => {
    if (!isFiltering) return listaIngresos;
    const start = (filteredPage - 1) * filteredPageSize;
    return listaIngresos.slice(start, start + filteredPageSize);
  }, [isFiltering, listaIngresos, filteredPage]);

  const changeFilteredPage = useCallback((newPage) => {
    if (newPage < 1 || newPage > totalFilteredPages) return;
    setFilteredPage(newPage);
  }, [totalFilteredPages]);

  /**
   * Renderiza el contenido principal
   */
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner />
        </div>
      );
    }

    if (listaIngresos.length === 0) {
      return (
        <Card className="w-100 text-center py-5 shadow-sm border-0">
          <Card.Body className="w-100">
            <FaInbox size={64} className="text-muted mb-3" />
            <h5 className="text-muted">No hay ingresos disponibles</h5>
            <p className="text-secondary">
              {filtrosActivos 
                ? 'No se encontraron resultados con los filtros aplicados. Intente con otros criterios.'
                : 'No hay ingresos registrados en este momento.'}
            </p>
          </Card.Body>
        </Card>
      );
    }

    return (
      <>
        {/* Indicador de filtros activos */}
        {filtrosActivos && Object.keys(filtrosActivos).length > 0 && (
          <Alert variant="info" className="w-100 d-flex align-items-center justify-content-between">
            <div>
              <strong>Filtros activos:</strong> {Object.keys(filtrosActivos).length} filtro(s) aplicado(s)
            </div>
          </Alert>
        )}

        {/* Tabla de ingresos */}
        <IngresoTable
          ingresos={pagedIngresos}
          onIngresoClick={handleOpenIngreso}
          onObservacionClick={handleObservacion}
        />

        {/* Paginación */}
        {isFiltering && totalFilteredPages > 1 && (
          <div className="mt-4 d-flex justify-content-center">
            <Pagination
              prev={filteredPage > 1}
              next={filteredPage < totalFilteredPages}
              total_pages={totalFilteredPages}
              changePage={changeFilteredPage}
              page={filteredPage}
            />
          </div>
        )}

        {!isFiltering && ingresos.total_pages > 1 && (
          <div className="mt-4">
            <Pagination
              prev={ingresos.prev}
              next={ingresos.next}
              total_pages={ingresos.total_pages}
              changePage={changePage}
              page={page}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col xs={12}>
          {/* Sección de Filtros */}
          <FiltrosIngreso 
            onFilter={handleFilter}
            onClear={handleClearFilters}
          />

          {/* Contenido Principal */}
          {renderContent()}
        </Col>
      </Row>

      {/* Modal de Observaciones */}
      <ObservacionModal
        show={showObservacionModal}
        onHide={() => setShowObservacionModal(false)}
        ingreso={ingresoSeleccionado}
      />
    </Container>
  );
};